import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'

import { Box, Button, Dialog, Grid, Tooltip, useTheme } from '@mui/material'
import { Add } from '@mui/icons-material'

import { 
  usePOST_groupCourseScheduleMutation, 
 } from 'redux/api/groupCoursesApi'
import { useGET_courseSelectQuery } from 'redux/api/coursesApi'
import { useGET_academicPeriodsSelectQuery } from 'redux/api/academicPeriodsApi'
import { useGET_teacherProfileSelectQuery } from 'redux/api/teachersApi'

import GroupCourseSchedule from 'components/molecules/GroupCourseSchedule'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import SelectField from 'components/atoms/fields/SelectField'
import DatePickerField from 'components/atoms/fields/DatePickerField'

import { useErrorAlert } from 'utils/customHooks/useErrorAlert'
import { useSuccessAlert } from 'utils/customHooks/useSuccessAlert'

import formModel, { initialValues, validationSchema } from 'utils/formModels/groupEnrollCourseModel'
import { crudColorMap, crudIconMap}  from 'utils/helpers'

import { ActionType, IGroupCourseData } from 'types'
import BoxTitle from 'components/atoms/BoxTitle'
import { useSelector } from 'react-redux'
import { RootState } from 'redux/store'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
}

const GroupEnrollCourseForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  
  const user = useSelector((state: RootState) => state['user'].user)

  const [open, setOpen] = useState(false)
  const [enrollCourse, {isLoading, error, isSuccess}] = usePOST_groupCourseScheduleMutation()
  const { course, academic_periods, teachers, date_start, date_end, schedule } = formModel()

  useErrorAlert(error)
  useSuccessAlert(isSuccess ? t('alerts.course-enrolled') : '')

  useEffect(() => {
    if (isSuccess) {
       setOpen(false);
    }
  }, [isSuccess])


  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'right',
        alignItems: 'center',
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
      }}
      className="transparent-background"
    >

      { ['principal','staff-administrator'].includes(user?.role) && 
      <Tooltip title={`${t('common.add')}`} arrow placement='top'>
        <Button
          variant='contained'
          color='success'
          aria-haspopup='true'
          onClick={() => setOpen(true)}
          disableElevation
          sx={{
            '&:hover': { background: theme.palette.success.main, border: 'none', '& svg': { color: '#000' } },
            height: '2.75rem',
            padding: '5',
            minWidth: 'auto',
            marginRight: '0.2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
          }}
        >
          <Add
            sx={{ fontSize: '2rem', lineHeight: '1' }}
          />
          <span style={{ marginLeft: '0.5rem', display: 'inline-block' }}>{`${t(
                      'common.enroll-course',
                    )}`}</span>

        </Button>
      </Tooltip>}


      <Dialog
                onClose={() => setOpen(false)}
                open={open}
                fullWidth
                maxWidth='md'
                PaperProps={{
                  sx: {
                    margin: 0,
                    padding: 10,
                    width: { xs: '100vw', md: '80vw' },
                    maxWidth: { xs: '100vw', md: '80vw' },
                    background: theme.palette.background.default,
                  },
                }}
              >
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {

            let schedule:any = {...values.schedule}

            Object.keys(schedule).forEach(function(key, index) {
              if(schedule[key].start_time?.id && schedule[key].duration_minutes){
                schedule[key] = {
                  start_time: schedule[key].start_time?.id ?? null,
                  duration_minutes: parseInt(schedule[key].duration_minutes)
                }; 
              }else{
                delete schedule[key];
              }
            });

            const data: IGroupCourseData = {
              group_id: parseInt(id),
              course_id: values.course.id,
              date_start: values.date_start,
              date_end: values.date_end,
              academic_periods_ids: values.academic_periods.map(el => el.id),
              teacher_ids: values.teachers.map(el => el.id),
              schedule: schedule
            }

            enrollCourse(data)
          }}
        >
          <Form>

            <BoxTitle title={`${t('common.enroll-course')}`} />

            <Grid container spacing={4} sx={{ marginTop: '0px' }}>

              <Grid item xs={12} lg={4}>
                <SelectField
                  fieldProps={{ ...course }}
                  apiCall={useGET_courseSelectQuery}
                  disabled={viewOnly}
                />
              </Grid>

              <Grid item xs={12} lg={4}>
                <DatePickerField fieldProps={{ ...date_start }} disabled={viewOnly} minDate={new Date()} />
              </Grid>

              <Grid item xs={12} lg={4}>
                <DatePickerField fieldProps={{ ...date_end }} disabled={viewOnly} minDate={new Date()} />
              </Grid>     

              <Grid item xs={12} lg={6}>
                <SelectField
                  multiple={true}
                  fieldProps={{ ...academic_periods }}
                  apiCall={useGET_academicPeriodsSelectQuery}
                  disabled={viewOnly}
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <SelectField
                  multiple={true}
                  fieldProps={{ ...teachers }}
                  apiCall={useGET_teacherProfileSelectQuery}
                  disabled={viewOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <GroupCourseSchedule fieldProps={{ ...schedule }} disabled={viewOnly} />
              </Grid>    


              <Grid item xs={12}>
                <Box sx={{ display: 'flex' }}>
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => setOpen(false)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('common.back')}`}
                  </Button>
                  {action !== 'view' && (
                    <SubmitButton
                      color={crudColorMap[action]}
                      text={`${t('common.enroll-course')}`}
                      icon={crudIconMap[action]}
                      isLoading={isLoading}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          </Form>
        </Formik>
      </Dialog>
    </Box>
  )
}
export default GroupEnrollCourseForm
